import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Exploration() {
  const services = [
    {
      icon: 'ri-radar-line',
      title: 'Seismic Surveys',
      description: 'Advanced 3D and 4D seismic acquisition and processing using latest imaging technologies.',
      capabilities: [
        'High-resolution 3D seismic surveys',
        'Time-lapse 4D monitoring',
        'Ocean bottom node (OBN) acquisition',
        'Full waveform inversion (FWI)',
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Exploratory Drilling',
      description: 'Precision drilling operations in challenging environments with proven success rates.',
      capabilities: [
        'Deepwater drilling to 3,000m',
        'High-pressure high-temperature (HPHT) wells',
        'Horizontal and directional drilling',
        'Real-time drilling optimization',
      ],
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Geological Analysis',
      description: 'Comprehensive subsurface characterization and reservoir modeling.',
      capabilities: [
        'Basin and play analysis',
        'Structural and stratigraphic interpretation',
        'Geochemical and petrophysical studies',
        'Reservoir quality assessment',
      ],
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Integration',
      description: 'AI-powered analytics combining geological, geophysical, and engineering data.',
      capabilities: [
        'Machine learning for prospect identification',
        'Integrated subsurface modeling',
        'Risk and uncertainty analysis',
        'Portfolio optimization',
      ],
    },
  ];

  const basins = [
    {
      name: 'West African Transform Margin',
      location: 'Ghana, Côte d\'Ivoire',
      status: 'Active Exploration',
      description: 'Deepwater turbidite plays with significant oil potential in underexplored acreage.',
      image: 'https://readdy.ai/api/search-image?query=Offshore%20seismic%20survey%20vessel%20conducting%20exploration%20operations%20in%20deep%20blue%20ocean%2C%20professional%20marine%20geophysical%20survey%20ship%20with%20equipment%20arrays%2C%20aerial%20view%20showing%20vessel%20wake%20and%20survey%20lines%2C%20modern%20petroleum%20exploration%20technology%20at%20sea%2C%20clear%20weather%20conditions&width=600&height=400&seq=basin-1&orientation=landscape',
    },
    {
      name: 'East African Rift System',
      location: 'Kenya, Tanzania',
      status: 'Appraisal Phase',
      description: 'Onshore and nearshore gas discoveries in Tertiary rift basins.',
      image: 'https://readdy.ai/api/search-image?query=Onshore%20drilling%20rig%20in%20African%20landscape%20with%20exploration%20equipment%2C%20land-based%20petroleum%20drilling%20operation%20in%20savanna%20terrain%2C%20professional%20industrial%20photography%20showing%20derrick%20and%20support%20facilities%2C%20clear%20sky%20and%20natural%20environment%2C%20wide%20angle%20view&width=600&height=400&seq=basin-2&orientation=landscape',
    },
    {
      name: 'Zambezi Delta',
      location: 'Mozambique',
      status: 'Development Planning',
      description: 'Major gas condensate accumulations in deepwater Cretaceous reservoirs.',
      image: 'https://readdy.ai/api/search-image?query=Deepwater%20oil%20platform%20in%20tropical%20waters%20with%20support%20vessels%2C%20modern%20offshore%20production%20facility%20in%20pristine%20blue%20ocean%2C%20aerial%20photography%20showing%20complete%20installation%20with%20helipad%2C%20professional%20petroleum%20infrastructure%20in%20coastal%20region&width=600&height=400&seq=basin-3&orientation=landscape',
    },
  ];

  const successStory = {
    title: 'Zambezi Basin Discovery',
    subtitle: 'From Seismic to Success',
    stats: [
      { value: '2,400 km²', label: 'Seismic Coverage' },
      { value: '180 MMboe', label: 'Recoverable Resources' },
      { value: '80%', label: 'Success Rate' },
      { value: '18 months', label: 'Exploration to Discovery' },
    ],
    description: 'Our Zambezi Basin exploration campaign demonstrates the power of integrated geoscience and advanced technology. Using proprietary seismic processing algorithms and AI-driven prospect identification, we identified a previously overlooked structural trap in a mature basin. Five exploratory wells confirmed commercial hydrocarbons in four locations, with the discovery well flowing at 8,500 barrels per day during testing.',
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.75)), url('https://readdy.ai/api/search-image?query=Seismic%20survey%20operations%20with%20geophysical%20equipment%20and%20data%20visualization%20displays%2C%20petroleum%20exploration%20technology%20showing%20subsurface%20imaging%20and%20geological%20analysis%2C%20professional%20technical%20photography%20in%20operations%20center%2C%20modern%20digital%20screens%20with%20seismic%20data%20interpretation&width=1920&height=600&seq=exploration-hero&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl lg:text-6xl font-bold mb-4">Exploration & Development</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Unlocking hydrocarbon potential through cutting-edge geoscience and proven drilling expertise
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Exploration Services & Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive upstream services from initial prospect generation to drilling success
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-xl hover:shadow-xl transition-all cursor-pointer">
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-emerald-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {service.capabilities.map((capability, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-lg mt-0.5"></i>
                      <span className="text-gray-700">{capability}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frontier Basins */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Frontier Basin Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Active exploration across Africa's most promising hydrocarbon provinces
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {basins.map((basin, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all cursor-pointer">
                <img
                  src={basin.image}
                  alt={basin.name}
                  className="w-full h-56 object-cover object-top"
                />
                <div className="p-6">
                  <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                    {basin.status}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{basin.name}</h3>
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <i className="ri-map-pin-line"></i>
                    <span>{basin.location}</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{basin.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Story */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-slate-900 text-white rounded-2xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-10 lg:p-16">
                <div className="inline-block px-4 py-2 bg-emerald-500 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  Success Story
                </div>
                <h2 className="text-4xl font-bold mb-4">{successStory.title}</h2>
                <p className="text-xl text-emerald-400 mb-8">{successStory.subtitle}</p>
                
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {successStory.stats.map((stat, index) => (
                    <div key={index}>
                      <div className="text-3xl font-bold text-emerald-400 mb-1">{stat.value}</div>
                      <div className="text-gray-300 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <p className="text-gray-300 leading-relaxed">{successStory.description}</p>
              </div>

              <div
                className="h-full min-h-96 bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Successful%20oil%20well%20testing%20with%20flowing%20hydrocarbons%2C%20petroleum%20exploration%20discovery%20moment%20with%20equipment%20and%20gauges%2C%20professional%20industrial%20photography%20showing%20production%20test%2C%20technical%20petroleum%20engineering%20scene%20with%20safety%20equipment%2C%20dramatic%20lighting&width=800&height=1000&seq=success-story&orientation=portrait')`,
                }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Partner on Your Next Exploration Project
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Leverage our expertise in frontier basin exploration and proven track record of discovery success
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Discuss Opportunities
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}